import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Language } from '@core/services';
import { TranslateModule } from '@ngx-translate/core';

export interface IContactModalData {
  phone: string;
  name: string;
  messageLabel?: string;
  messageParams?: Record<string, string>;
  modalTitleLabel?: string;
  modalDescriptionLabel?: string;
  modalActionLabel?: string;
}

/**
 * Modal component for displaying contact information and providing a shortcut to WhatsApp.
 */
@Component({
  selector: 'app-contact-modal',
  imports: [MatDialogModule, TranslateModule],
  templateUrl: './contact-modal.component.html',
  styleUrl: './contact-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactModal {
  /**
   * Reference to the dialog instance.
   */
  public readonly dialogRef = inject(MatDialogRef<ContactModal>);

  /**
   * Language service for handling localization.
   */
  private readonly language = inject(Language);

  /**
   * Data injected into the modal, containing the phone number.
   */
  public readonly data: IContactModalData = inject(MAT_DIALOG_DATA);

  /** Etiquetas configurables para adaptar el modal al contexto que lo abre. */
  protected readonly modalTitleLabel = this.data.modalTitleLabel || '$contact';
  protected readonly modalActionLabel = this.data.modalActionLabel || '$continuar';

  /**
   * Signal holding the phone number to be used in the modal.
   */
  protected readonly phone = signal<string>(this.data.phone);

  /**
   * Opens WhatsApp in a new tab using the provided phone number.
   * Removes spaces, parentheses, plus sign, and other special characters from the phone number.
   */
  protected async goToWhatsApp(): Promise<void> {
    const messageLabel = this.data.messageLabel || 'message_whatsapp';
    const messageParams = this.data.messageParams || { value: this.data.name };
    const text = await this.language.getTranslate(messageLabel, messageParams);

    // Remove spaces, parentheses, plus sign and any other special characters
    const cleanPhone = this.phone().replace(/\D/g, '');
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(text)}`;

    window.open(whatsappUrl, '_blank');
  }
}
